import React, { useState, useContext, useEffect, useMemo } from 'react';
import { CheckCircleOutline, NotInterested, InfoOutlined, Search } from '@mui/icons-material';
import {
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Paper,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import DataTable from '../../../dataTable';
import { IDataTableColumn } from '../../../dataTable/DataTable/model';
// Import context components with explicit typed import declaration
import { DevicesContext, DevicesProvider, DevicesContextType } from './contexts';
import { updateDevice } from './services';
import EditDeviceForm from './components/EditDeviceForm';

// Interface for device data
export interface IDevice {
  id: number;
  serial: string;
  location: string;
  last_check_in: string;
  primary_company: string;
  secondary_company?: string;
  tertiary_company?: string;
}

// Create a separate component for the devices content
const DevicesContent: React.FunctionComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();

  const { devices, loading, refreshDevices } = useContext<DevicesContextType>(DevicesContext);
  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);
  const [loadingDevice, setLoadingDevice] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedDevice, setSelectedDevice] = useState<IDevice | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);

  // Load devices only once on component mount
  useEffect(() => {
    if (!initialDataLoaded) {
      refreshDevices(true); // Always include inactive devices
      setInitialDataLoaded(true);
    }
  }, [initialDataLoaded]);

  // Navigate to device details page
  const handleViewDevice = (device: IDevice) => {
    history.push(`/edgelink/admin/devices/${device.serial}`, { hasData: true, device });
  };

  // Open edit device dialog
  const handleEditDevice = (device: IDevice, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent row click from triggering if coming from icon click
    }
    setSelectedDevice(device);
    setEditDialogOpen(true);
  };

  // Close edit device dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedDevice(null);
  };

  // Save updated device details
  const handleSaveDevice = async (updatedDevice: IDevice) => {
    try {
      // The companySelections property contains the ID mappings added by EditDeviceForm
      const companySelections = (updatedDevice as any).companySelections || {};

      // Update device with company IDs
      await updateDevice(updatedDevice.id, companySelections);

      // Refresh the device list
      await refreshDevices(true);
    } catch (error) {
      console.error('Error saving device:', error);
    }
  };

  // Define table columns
  const columns: IDataTableColumn[] = [
    {
      name: 'Serial',
      selector: (row: IDevice) => row.serial,
      sortable: true,
      wrap: true,
      grow: 1,
      width: '8rem',
    },
    {
      name: 'Location',
      selector: (row: IDevice) => row.location,
      sortable: true,
      wrap: true,
      grow: 1,
      width: '15rem',
    },
    {
      name: 'Last Check-in',
      selector: (row: IDevice) => row.last_check_in,
      sortable: true,
      cell: (row: IDevice) => {
        const date = new Date(row.last_check_in);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();

        // Calculate time ago
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);

        // Format time ago text
        let timeAgo = '';
        if (diffDay > 0) {
          timeAgo = `${diffDay} ${diffDay === 1 ? 'day' : 'days'} ago`;
        } else if (diffHour > 0) {
          timeAgo = `${diffHour} ${diffHour === 1 ? 'hour' : 'hours'} ago`;
        } else if (diffMin > 0) {
          timeAgo = `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`;
        } else {
          timeAgo = 'just now';
        }

        return (
          <span>
            {date.toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
            <br />
            <Typography variant="caption" color="text.secondary">
              {timeAgo}
            </Typography>
          </span>
        );
      },
      grow: 1,
      width: '20rem',
    },
    {
      name: 'Primary Company',
      selector: (row: IDevice) => row.primary_company,
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: 'Secondary Company',
      selector: (row: IDevice) => row.secondary_company || '',
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: 'Tertiary Company',
      selector: (row: IDevice) => row.tertiary_company || '',
      sortable: true,
      wrap: true,
      grow: 1,
    },
  ];

  // Filter devices based on search text
  const filteredDevices = useMemo(() => {
    if (!searchText.trim()) {
      return devices;
    }

    const searchLowerCase = searchText.toLowerCase();

    return devices.filter((device) => {
      // Check if any column (except status) contains the search text
      return (
        (device.serial?.toLowerCase() || '').includes(searchLowerCase) ||
        (device.location?.toLowerCase() || '').includes(searchLowerCase) ||
        (device.last_check_in?.toLowerCase() || '').includes(searchLowerCase) ||
        (device.primary_company?.toLowerCase() || '').includes(searchLowerCase) ||
        (device.secondary_company?.toLowerCase() || '').includes(searchLowerCase) ||
        (device.tertiary_company?.toLowerCase() || '').includes(searchLowerCase)
      );
    });
  }, [devices, searchText]);

  // Show loading when fetching data
  if (loadingDevice) {
    return <CircularProgress />;
  }

  // Use base columns array directly
  const finalColumns = columns;

  // Adjust columns based on screen size
  const responsiveColumns = isMobile
    ? columns.filter(
        (col) =>
          // Keep only essential columns for mobile view
          col.name === 'Status' || col.name === 'Serial' || col.name === 'Location',
      )
    : finalColumns;

  return (
    <>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'stretch' : 'center'}
        spacing={isMobile ? 1 : 2}
        mb={isMobile ? 1 : 2}
      >
        <Box>
          <Typography variant="h1" component="h1" m={0} fontSize={isMobile ? '1.5rem' : 'inherit'}>
            Devices Management
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              mt: 1,
              mb: 1,
              color: theme.palette.text.secondary,
              flexWrap: 'wrap',
              fontSize: isMobile ? '0.8rem' : 'inherit',
            }}
          >
            <InfoOutlined fontSize="small" sx={{ mr: 1, mt: '2px' }} />
            <Typography variant="body2" fontSize="inherit">
              Click on a row to edit device details.
            </Typography>
          </Box>
        </Box>

        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems="center"
          spacing={isMobile ? 1 : 2}
          width={isMobile ? '100%' : 'auto'}
          mt={isMobile ? 1 : 0}
        >
          <Paper
            elevation={1}
            sx={{
              p: '2px 4px',
              display: 'flex',
              alignItems: 'center',
              width: isMobile ? '100%' : '300px',
              borderRadius: '4px',
            }}
          >
            <TextField
              placeholder="Search devices..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" fontSize="small" />
                  </InputAdornment>
                ),
                sx: { borderRadius: '4px' },
              }}
            />
          </Paper>
        </Stack>
      </Stack>
      <Box position="relative" sx={{ overflowX: 'auto' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <DataTable
          columns={isMobile ? responsiveColumns : finalColumns}
          data={filteredDevices}
          noHeader
          striped
          highlightOnHover
          pointerOnHover
          dense={isMobile}
          onRowClicked={handleEditDevice}
          conditionalRowStyles={[
            {
              when: (row) => !row.status,
              style: {
                backgroundColor: theme.palette.action.hover,
                opacity: 0.8,
              },
            },
          ]}
        />

        {/* Edit Device Dialog */}
        <EditDeviceForm
          device={selectedDevice}
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          onSave={handleSaveDevice}
        />
      </Box>
    </>
  );
};

// Main devices component that wraps the content with the provider
const DevicesManagementPage: React.FunctionComponent = () => {
  return (
    <DevicesProvider>
      <DevicesContent />
    </DevicesProvider>
  );
};

export default DevicesManagementPage;
